/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { DatePicker, Drawer } from "antd";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "../../../../Common/Svgs/CloseIcon";
import getUserType from "../../getUserType";
import { ExportSecondaryIcon } from "../../../../Common/Svgs/ExportIcon";
import { updateRevenue } from "../../../../Redux/Slices/revenue.slice";
import { PAYMENT_RECEIVER_TYPE } from "../../data";
import { client } from "../../../../Utils/axiosClient";
import { ColumnHeader, EmptyState } from "./Common";
import handleFarmerExport from "./handleFarmerExport";
import handleDriverExport from "./handleDriverExport";
import DataList from "./DataList";
import ChevronIcon from "../../../../Common/Svgs/ChevronIcon";

const ExportDrawer = ({ visible, onCancel, selectedView }) => {
  const dispatch = useDispatch();

  const { loading } = useSelector((state) => state.revenue);

  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sortOrder, setSortOrder] = useState("asc");
  const [open, setOpen] = useState({
    start: false,
    end: false,
  });
  const [dates, setDates] = useState({
    start: "",
    end: "",
  });

  const disabled = dates?.start === "" || dates?.end === "";

  const allSelected =
    data?.length > 0 && data.every((d) => selected.includes(d.id));

  const handleClose = () => {
    setSelected([]);
    setData([]);
    setDates({
      start: "",
      end: "",
    });

    onCancel();
  };

  const onDateSelect = (e, type = "start") => {
    setDates((prev) => ({ ...prev, [type]: e }));
  };

  const onColumnCheckboxClick = () => {
    setSelected(() => {
      if (allSelected) {
        return [];
      }

      return data.map((d) => d.id);
    });
  };

  const onSortClick = () => {
    setData((prev) => {
      // toggle the sort order: asc → desc → asc
      const nextOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(nextOrder);

      // sorting by firstName
      const sorted = [...prev].sort((a, b) => {
        const nameA = a.firstName?.toLowerCase?.() || "";
        const nameB = b.firstName?.toLowerCase?.() || "";

        if (nameA < nameB) return nextOrder === "asc" ? -1 : 1;
        if (nameA > nameB) return nextOrder === "asc" ? 1 : -1;
        return 0;
      });

      return sorted;
    });
  };

  const onExportClick = () => {
    if (dates?.start === "" || dates?.end === "" || data?.length === 0) return;

    if (selectedView === PAYMENT_RECEIVER_TYPE.FARMER) {
      const farmerIds =
        selected.length === 0 ? data.map((d) => d.id) : [...selected];

      handleFarmerExport({
        farmerIds,
        startDate: dates?.start,
        endDate: dates?.end,
        dispatch,
        closeDrawer: handleClose,
      });
    }

    if (selectedView === PAYMENT_RECEIVER_TYPE.DRIVER) {
      const driverIds =
        selected.length === 0 ? data.map((d) => d.id) : [...selected];

      handleDriverExport({
        driverIds,
        startDate: dates?.start,
        endDate: dates?.end,
        dispatch,
        closeDrawer: handleClose,
      });
    }
  };

  const getData = useCallback(async () => {
    try {
      dispatch(
        updateRevenue({
          key: "loading",
          value: true,
        }),
      );

      if (dates?.start === "" || dates?.end === "") return;

      const BASE_URL =
        selectedView === PAYMENT_RECEIVER_TYPE.FARMER
          ? `${process.env.REACT_APP_API_URL}/revenue/get-export-farmer-names`
          : `${process.env.REACT_APP_API_URL}/revenue/get-export-driver-names`;

      const response = await client.get(BASE_URL, {
        params: {
          startDate: dates?.start,
          endDate: dates?.end,
        },
      });

      if (response.status === 200) {
        setData([...response.data.result]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(
        updateRevenue({
          key: "loading",
          value: false,
        }),
      );
    }
  }, [dispatch, dates, selectedView]);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <Drawer
      width={448}
      placement="right"
      closable={false}
      maskStyle={{
        background: "transparent",
        boxShadow: "12px 12px 16px -4px #10182814",
      }}
      open={visible}
      onClose={handleClose}
      className="rounded-tl-xl"
    >
      <div className="bg-background-white w-full h-[calc(100vh-72px)] overflow-y-hidden rounded-tl-xl">
        <div className="max-h-max">
          <div className="flex items-center justify-between pt-6 px-6">
            <div className="flex items-center gap-x-3">
              <p className="font-poppins font-medium text-base text-black">
                Export
              </p>

              {getUserType(selectedView)}
            </div>

            <CloseIcon
              className="w-5 h-5 cursor-pointer stroke-dark-grey-text"
              onClick={handleClose}
            />
          </div>

          <div className="mt-3 px-6 flex items-center gap-x-3">
            <DatePicker
              inputReadOnly
              open={open.start}
              format="DD MMM YYYY"
              placeholder="Start Date"
              clearIcon={null}
              value={dates?.start ? dayjs(dates.start) : null}
              className="bg-green-10 border border-green-10 h-9"
              disabledDate={(current) => current.isAfter(dayjs())}
              onChange={(e) => onDateSelect(e, "start")}
              suffixIcon={
                <ChevronIcon
                  className={`w-4 h-4 stroke-green transform transition-all duration-200 ${
                    open.start ? "-rotate-90" : "rotate-90"
                  }`}
                />
              }
              onOpenChange={(state) =>
                setOpen((prev) => ({ ...prev, start: state }))
              }
            />

            <DatePicker
              inputReadOnly
              open={open.end}
              format="DD MMM YYYY"
              placeholder="End Date"
              clearIcon={null}
              value={dates?.end ? dayjs(dates.end) : null}
              className="bg-green-10 border border-green-10 h-9 "
              disabledDate={(current) => current.isAfter(dayjs())}
              onChange={(e) => onDateSelect(e, "end")}
              suffixIcon={
                <ChevronIcon
                  className={`w-4 h-4 stroke-green transform transition-all duration-200 ${
                    open.end ? "-rotate-90" : "rotate-90"
                  }`}
                />
              }
              onOpenChange={(state) =>
                setOpen((prev) => ({ ...prev, end: state }))
              }
            />

            <button
              type="button"
              disabled={disabled || loading}
              className={`px-4 py-2 h-9 flex items-center gap-x-2 rounded-md
                ${
                  disabled
                    ? "bg-light-grey text-[#B1B1B1] cursor-not-allowed"
                    : "bg-yellow hover:bg-yellow-dark text-black"
                }
                `}
              onClick={onExportClick}
            >
              <ExportSecondaryIcon
                className={` ${disabled ? "text-[#B1B1B1]" : "text-black"}`}
              />

              <p className="font-poppins text-xs leading-4.5 font-medium">
                Export
              </p>
            </button>
          </div>

          {data?.length ? (
            <div className="mt-4 h-full">
              <ColumnHeader
                selected={allSelected}
                onCheckboxClick={onColumnCheckboxClick}
                onSortClick={onSortClick}
              />

              <DataList
                data={data}
                selected={selected}
                setSelected={setSelected}
              />
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default ExportDrawer;
